// frontend/src/App.js
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Container, Navbar, Button, Nav, Tab, Spinner, Modal } from 'react-bootstrap';
import { FaUniversalAccess } from 'react-icons/fa';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import GoogleSignIn from './components/GoogleSignIn';
import LoginPage from './components/LoginPage';
import DesignForm from './components/DesignForm';
import UnifiedWorkflowCreator from './components/UnifiedWorkflowCreator';
import AgentCreator from './components/AgentCreator';
import DeployedTools from './components/DeployedTools';
import { API_URL, GOOGLE_CLIENT_ID } from './config';

function AppContent() {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('workflow');
  const [showAccessibility, setShowAccessibility] = useState(false);
  const workflowRef = useRef(null);
  const agentCreatorRef = useRef(null);
  const handleFinalize = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/finalize`, {});
      alert(response.data.message || "Entry saved successfully!");
    } catch (error) {
      console.error("Error finalizing entry:", error);
      alert("Failed to save entry.");
    }
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  const appStyles = {
    minHeight: '100vh',
  };

  const tabStyles = `
    #main-tabs .nav-tabs {
      border-bottom: 2px solid #dee2e6;
    }
    #main-tabs .nav-tabs .nav-link {
      font-weight: 600;
      font-size: 1rem;
      padding: 10px 20px;
      color: #555;
      border: 1px solid transparent;
      border-radius: 8px 8px 0 0;
      white-space: nowrap;
    }
    #main-tabs .nav-tabs .nav-link:hover {
      color: #007bff;
      border-color: #e9ecef #e9ecef #dee2e6;
    }
    #main-tabs .nav-tabs .nav-link.active {
      color: #007bff;
      font-weight: 700;
      border-color: #dee2e6 #dee2e6 #fff;
    }
  `;

  // User is authenticated, show full app
  return (
        <div className="App" style={appStyles}>
          <style>{tabStyles}</style>
          <a href="#main-content" className="skip-link">Skip to main content</a>
          {/* Navbar */}
          <Navbar bg="primary" variant="dark" expand="lg">
            <Container>
              <Navbar.Brand>Accessible Design Tool</Navbar.Brand>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '10px', rowGap: '8px' }}>
                <GoogleSignIn />
                <Button
                  variant="outline-light"
                  onClick={() => setShowAccessibility(true)}
                  aria-haspopup="dialog"
                >
                  <FaUniversalAccess className="me-1" aria-hidden="true" />
                  Accessibility
                </Button>
                <Button variant="outline-light" className="me-2">Reset</Button>
                <Button variant="outline-light" onClick={handleFinalize}>Finalize</Button>
              </div>
            </Container>
          </Navbar>

      <Container as="main" id="main-content" fluid className="py-3">
        <div id="main-tabs">
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="workflow">Workflow Creator</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="agentcreator">Agent Creator</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="deployed">Deployed Tools</Nav.Link>
            </Nav.Item>
          </Nav>
          
          <Tab.Content>
            <Tab.Pane eventKey="workflow">
              <UnifiedWorkflowCreator 
                ref={workflowRef}
                onCreateAgent={(agentData) => {
                  setActiveTab('agentcreator');
                  setTimeout(() => {
                    if (agentCreatorRef.current?.prefillFromWizard) {
                      agentCreatorRef.current.prefillFromWizard(agentData);
                    }
                  }, 100);
                }}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="agentcreator">
              <AgentCreator ref={agentCreatorRef} />
            </Tab.Pane>

            <Tab.Pane eventKey="deployed">
              <DeployedTools 
                onEditWorkflow={(workflow) => {
                  setActiveTab('workflow');
                  setTimeout(() => {
                    if (workflowRef.current?.loadWorkflowForEditing) {
                      workflowRef.current.loadWorkflowForEditing(workflow);
                    }
                  }, 100);
                }}
                onCloneWorkflow={(workflow) => {
                  setActiveTab('workflow');
                  setTimeout(() => {
                    if (workflowRef.current?.loadWorkflowForCloning) {
                      workflowRef.current.loadWorkflowForCloning(workflow);
                    }
                  }, 100);
                }}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
        </div>
      </Container>

      <Modal show={showAccessibility} onHide={() => setShowAccessibility(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUniversalAccess className="me-2" aria-hidden="true" />
            Accessibility
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DesignForm onClose={() => setShowAccessibility(false)} />
        </Modal.Body>
      </Modal>
        </div>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AccessibilityProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </AccessibilityProvider>
    </GoogleOAuthProvider>
  );
}

export default App;






